import { z } from 'zod';
/** Core demo-data slice — the shared ledger inputs every screen reads through `trades()`, plus the entity tables the shell,
 *  dashboard, prepare, risk, TradeHub, manage and misc surfaces render (SDD §7.4 entities). Transcribed verbatim from the
 *  design reference `renderVals()` (script lines 3727–5158) and members EX_TRADES/REV_DAYS/REV_ACCTS (382–395, 813–836). */
export const ExTradeRowSchema = z.tuple([
  z.number(),
  z.number(),
  z.string(),
  z.string(),
  z.string(),
  z.number(),
  z.string(),
  z.string(),
  z.number(),
  z.number(),
  z.string(),
  z.number(),
  z.string(),
]);
export const RevEntrySchema = z.tuple([
  z.string(),
  z.string(),
  z.number(),
  z.number(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.array(z.string()),
]);
export const RevDaySchema = z
  .object({
    label: z.string(),
    short: z.string(),
    mood: z.string(),
    moodIcon: z.string(),
    energy: z.number(),
    entries: z.array(RevEntrySchema),
    ai: z.tuple([z.string(), z.string()]),
  })
  .strict();
export const RevAcctSchema = z.tuple([
  z.string(),
  z.string(),
  z.number(),
  z.number(),
  z.number(),
  z.number(),
  z.number(),
  z.number(),
  z.string(),
]);

export const AccountSchema = z
  .object({
    firm: z.string(),
    plan: z.string(),
    color: z.string(),
    status: z.enum(['funded', 'challenge', 'paused', 'live']),
    phase: z.string().optional(),
    /** Null is "no data", never zero: an account this build cannot read a balance for (a linked shell — no
     *  live broker link exists) says so, instead of wearing an invented figure beside a real account number. */
    equity: z.number().nullable(),
    pnl: z.number().nullable(),
    prog: z.number().optional(),
    target: z.number().optional(),
    dd: z.number(),
    ddMax: z.number(),
    days: z.number(),
    wins: z.number(),
    id: z.string(),
  })
  .strict();
export const LedgerRowSchema = z
  .object({
    date: z.string(),
    ev: z.string(),
    evCol: z.string(),
    firm: z.string(),
    detail: z.string(),
    amt: z.string(),
    amtCol: z.string(),
  })
  .strict();
export const ParamSchema = z.object({ k: z.string(), v: z.string() }).strict();
export const RuleMetaSchema = z
  .object({
    k: z.string(),
    icon: z.string(),
    name: z.string(),
    type: z.string(),
    desc: z.string(),
    params: z.array(ParamSchema),
  })
  .strict();
export const RiskControlMetaSchema = z
  .object({
    name: z.string(),
    value: z.string(),
    scope: z.string(),
    icon: z.string(),
    desc: z.string(),
    params: z.array(ParamSchema),
  })
  .strict();
export const DashTradeSchema = z.tuple([
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
]);
export const SignalSchema = z
  .object({
    sym: z.string(),
    dir: z.string(),
    conf: z.string(),
    dirFg: z.string(),
    dirBg: z.string(),
    entry: z.string(),
    sl: z.string(),
    tp: z.string(),
    rr: z.string(),
    note: z.string(),
  })
  .strict();
export const NewsSchema = z
  .object({ src: z.string(), when: z.string(), title: z.string(), body: z.string() })
  .strict();
export const CalendarSchema = z
  .object({ time: z.string(), ccy: z.string(), event: z.string(), impact: z.string(), fg: z.string() })
  .strict();
export const PosRowSchema = z.tuple([
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.number(),
]);
export const NotifSchema = z
  .object({
    type: z.string(),
    icon: z.string(),
    col: z.string(),
    bg: z.string(),
    title: z.string(),
    body: z.string(),
    when: z.string(),
    unread: z.boolean(),
  })
  .strict();
export const SetNavSchema = z
  .object({ id: z.string(), label: z.string(), icon: z.string(), danger: z.boolean().optional() })
  .strict();
export const ConvGroupSchema = z
  .object({
    when: z.string(),
    items: z.array(z.object({ t: z.string(), d: z.string(), on: z.boolean().optional() }).strict()),
  })
  .strict();
export const ScorePillarSchema = z
  .object({ label: z.string(), value: z.number(), delta: z.number(), desc: z.string() })
  .strict();
export const ScoreMovedSchema = z
  .object({ t: z.string(), cat: z.string(), d: z.string(), v: z.string(), pos: z.boolean() })
  .strict();
export const KvSchema = z.tuple([z.string(), z.string()]).or(z.tuple([z.string(), z.string(), z.string()]));
export const ReplayAiSchema = z
  .object({ label: z.string(), score: z.string(), tag: z.string(), col: z.string(), narr: z.string() })
  .strict();
export const KpiSchema = z.object({ label: z.string(), value: z.string(), col: z.string() }).strict();
export const InvestorSchema = z
  .object({
    init: z.string(),
    name: z.string(),
    offer: z.string(),
    tag: z.string(),
    tagCol: z.string(),
    tagBg: z.string(),
    cta: z.string(),
    grad: z.string(),
    ink: z.string(),
  })
  .strict();
export const JEntrySchema = z
  .object({
    sym: z.string(),
    side: z.string(),
    r: z.string(),
    pnl: z.string(),
    pos: z.boolean(),
    strat: z.string(),
    time: z.string(),
    graded: z.string(),
    note: z.string(),
    tags: z.array(z.string()),
  })
  .strict();
export const StatSchema = z
  .object({ label: z.string(), value: z.string(), col: z.string(), sub: z.string() })
  .strict();
export const AttrSchema = z
  .tuple([z.string(), z.number(), z.number()])
  .or(z.tuple([z.string(), z.number(), z.number(), z.boolean()]));
export const SessionSchema = z
  .object({
    name: z.string(),
    timeShort: z.string().optional(),
    time: z.string().optional(),
    cls: z.string().optional(),
    title: z.string().optional(),
    dot: z.string(),
    fg: z.string(),
    bd: z.string(),
    bg: z.string(),
  })
  .strict();
export const TickerSchema = z
  .object({ pair: z.string(), px: z.string(), chg: z.string(), col: z.string() })
  .strict();
export const StatCardSchema = z
  .object({
    label: z.string(),
    value: z.string(),
    col: z.string(),
    sub: z.string(),
    ring: z.boolean(),
    dash: z.string().optional(),
    ringLabel: z.string().optional(),
  })
  .strict();

export const CoreDataSchema = z
  .object({
    exTrades: z.array(ExTradeRowSchema),
    revDays: z.array(RevDaySchema),
    revAccts: z.array(RevAcctSchema),
    accounts: z.array(AccountSchema),
    acctLedger: z.array(LedgerRowSchema),
    rulesMeta: z.array(RuleMetaSchema),
    rcMeta: z.record(z.string(), RiskControlMetaSchema),
    dashTrades: z.array(DashTradeSchema),
    signals: z.array(SignalSchema),
    news: z.array(NewsSchema),
    calendar: z.array(CalendarSchema),
    posPending: z.array(PosRowSchema),
    posClosed: z.array(PosRowSchema),
    posOrders: z.array(PosRowSchema),
    notifList: z.array(NotifSchema),
    setNav: z.array(SetNavSchema),
    setPrefs: z.array(z.tuple([z.string(), z.string(), z.string()])),
    aiConvGroups: z.array(ConvGroupSchema),
    scorePillars: z.array(ScorePillarSchema),
    scoreMoved: z.array(ScoreMovedSchema),
    replayMeta: z.array(KvSchema),
    replaySess: z.array(z.tuple([z.string(), z.string()])),
    replayAi: z.array(ReplayAiSchema),
    eodKpis: z.array(KpiSchema),
    eodHourly: z.array(z.tuple([z.string(), z.number(), z.boolean()])),
    eodRules: z.array(z.object({ t: z.string(), ok: z.boolean() }).strict()),
    scoreInvestors: z.array(InvestorSchema),
    jEntries: z.array(JEntrySchema),
    anStats: z.array(StatSchema),
    anSym: z.array(AttrSchema),
    anStrat: z.array(AttrSchema),
    anSess: z.array(AttrSchema),
    sessions: z.array(SessionSchema),
    sessionsOld: z.array(SessionSchema),
    ticker: z.array(TickerSchema),
    statCards: z.array(StatCardSchema),
    dailyBars: z.array(z.object({ amt: z.string(), h: z.number() }).strict()),
    subtitle: z.record(z.string(), z.string()),
  })
  .strict();
export type CoreData = z.infer<typeof CoreDataSchema>;
export type ExTradeRow = z.infer<typeof ExTradeRowSchema>;
export type RevDay = z.infer<typeof RevDaySchema>;
export type RevEntry = z.infer<typeof RevEntrySchema>;
export type Account = z.infer<typeof AccountSchema>;
